import { mutationOptions, queryOptions } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import {
    CUSTOMER_NOTIFICATIONS_LIST_LIMIT,
    customerNotificationsQueryKeyRoot,
} from '@/entities/customer-notification/api/customer-notifications/constants';
import {
    getAuthenticatedCustomerNotifications,
    getCustomerNotifications,
} from '@/entities/customer-notification/api/customer-notifications/server-actions';
import type { GetCustomerNotificationsParams } from '@/entities/customer-notification/api/customer-notifications/types';
import { getCustomerOrders, getOrder } from '@/entities/order/api/orders';
import { getAllergens } from '@/entities/product/api/get-allergens';
import { getMenuLists, getAggregatedMenuItems } from '@/entities/product/api/get-menu-lists';
import { getDeliveryMethods } from '@/entities/venue/api/get-delivery-methods';
import { getOutages } from '@/entities/venue/api/get-outages';
import { getPaymentMethods } from '@/entities/venue/api/get-payment-methods';
import { getVenue } from '@/entities/venue/api/get-venue';
import { getVenueOpeningHours } from '@/entities/venue/api/get-venue-opening-hours';
import {
    createCustomerAddress,
    deleteCustomerAddress,
    geocodeAddress,
    getCustomerAddresses,
    reverseGeocodeAddress,
    updateCustomerAddress,
} from '@/features/address/api/addresses';
import {
    createCustomerAvatarUploadUrl,
    deleteCustomerAvatar,
    finalizeCustomerAvatarUpload,
    finishTelephoneVerification,
    finishUserTelephoneVerification,
    getCustomer,
    getIsTelephoneVerified,
    getUserTelephoneVerificationStatus,
    loginCustomer,
    logoutCustomer,
    refreshCustomerAccessToken,
    registerCustomer,
    requestPasswordReset,
    resetPassword,
    startTelephoneVerification,
    startUserTelephoneVerification,
    updateCustomer,
} from '@/features/auth/api/customer';
import { getBlogPost, getBlogPosts } from '@/features/blog/api/get-blog-posts';
import { getCustomerCoupons } from '@/features/coupons/api/coupons';
import {
    addCustomerFavoriteItem,
    getCustomerFavoriteItems,
    removeCustomerFavoriteItem,
} from '@/features/favorites/api/favorites';
import {
    createOrder,
    getTimedOrderDiscount,
    getTimedOrderDiscounts,
    type TimedOrderDiscountParameters,
    type TimedOrderDiscountsParameters,
} from '@/features/order/api/create-order';
import { apiFetch } from '@/shared/api/api-fetch';
import { getQueryClient } from '@/shared/api/query-client';
import { PAGINATION_LIMIT } from '@/shared/config/constants/api';
import { getLogger } from '@/shared/lib/logger.client';
import { Country } from '@/shared/model/Country';
import { Language } from '@/shared/model/Language';

const logger = getLogger('query/api');

const VERIFIED_PHONE_TOKEN_STORAGE_KEY = 'verifiedPhoneToken';

export const QueryApi = {
    MenuList: {
        queries: {
            MenuLists: queryOptions({
                queryKey: ['MENU_LISTS'],
                queryFn: getMenuLists,
            }),
            AggregatedMenuItems: queryOptions({
                queryKey: ['AGGREGATED_MENU_ITEMS'],
                queryFn: getAggregatedMenuItems,
            }),
        },
    },
    Allergens: {
        queries: {
            Allergens: queryOptions({
                queryKey: ['ALLERGENS'],
                queryFn: getAllergens,
            }),
        },
    },
    Venue: {
        queries: {
            Venue: queryOptions({
                queryKey: ['VENUE'],
                queryFn: getVenue,
            }),
            OpeningHours: queryOptions({
                queryKey: ['VENUE_OPENING_HOURS'],
                queryFn: getVenueOpeningHours,
            }),
        },
    },
    PaymentMethods: {
        queries: {
            PaymentMethods: queryOptions({
                queryKey: ['PAYMENT_METHODS'],
                queryFn: getPaymentMethods,
            }),
        },
    },
    DeliveryMethods: {
        queries: {
            DeliveryMethods: queryOptions({
                queryKey: ['DELIVERY_METHODS'],
                queryFn: getDeliveryMethods,
            }),
        },
    },
    Customer: {
        queries: {
            Customer: queryOptions({
                queryKey: ['CUSTOMER'],
                queryFn: getCustomer,
            }),
            TelephoneVerificationStatus: queryOptions({
                queryKey: ['CUSTOMER_TELEPHONE_VERIFICATION_STATUS'],
                queryFn: getUserTelephoneVerificationStatus,
            }),
            Orders: Object.assign(
                ({ offset, limit }: { offset: number; limit: number }) =>
                    queryOptions({
                        queryKey: ['CUSTOMER_ORDERS', offset, limit],
                        queryFn: () => getCustomerOrders({ offset, limit }),
                    }),
                { queryKey: ['CUSTOMER_ORDERS'] as const },
            ),
            FavoriteItems: ({ offset, limit }: { offset: number; limit: number }) =>
                queryOptions({
                    queryKey: ['CUSTOMER_FAVORITE_ITEMS', offset, limit],
                    queryFn: () => getCustomerFavoriteItems({ offset, limit }),
                }),
            Addresses: ({ offset, limit }: { offset: number; limit: number }) =>
                queryOptions({
                    queryKey: ['CUSTOMER_ADDRESSES'],
                    queryFn: () => getCustomerAddresses({ offset, limit }),
                }),
            Coupons: ({ offset, limit }: { offset: number; limit: number }) =>
                queryOptions({
                    queryKey: ['CUSTOMER_COUPONS', offset, limit],
                    queryFn: () => getCustomerCoupons({ offset, limit }),
                }),
        },
        mutations: {
            Login: mutationOptions({
                mutationKey: ['LOGIN_CUSTOMER'],
                mutationFn: loginCustomer,
                onSuccess: () => {
                    getQueryClient().invalidateQueries({
                        queryKey: QueryApi.Customer.queries.Customer.queryKey,
                    });
                },
            }),
            Register: mutationOptions({
                mutationKey: ['REGISTER_CUSTOMER'],
                mutationFn: registerCustomer,
                onSuccess: () => {
                    getQueryClient().invalidateQueries({
                        queryKey: QueryApi.Customer.queries.Customer.queryKey,
                    });
                },
            }),
            Logout: mutationOptions({
                mutationKey: ['LOGOUT'],
                mutationFn: logoutCustomer,
                onMutate: () => {
                    getQueryClient().setQueryData(QueryApi.Customer.queries.Customer.queryKey, null);
                },
                onSuccess: () => {
                    getQueryClient().invalidateQueries({
                        queryKey: [...customerNotificationsQueryKeyRoot],
                    });
                },
            }),
            Update: mutationOptions({
                mutationKey: ['UPDATE_CUSTOMER'],
                mutationFn: updateCustomer,
                onMutate: (variables) => {
                    const queryClient = getQueryClient();
                    const originalData = queryClient.getQueryData<Awaited<ReturnType<typeof getCustomer>>>([
                        'CUSTOMER',
                    ]);
                    queryClient.setQueryData(QueryApi.Customer.queries.Customer.queryKey, (old) => {
                        return {
                            ...old!,
                            ...variables,
                        };
                    });
                    return originalData;
                },
                onError: (_, __, originalData) => {
                    getQueryClient().setQueryData(QueryApi.Customer.queries.Customer.queryKey, originalData);
                },
                onSuccess: (data) => {
                    getQueryClient().setQueryData(QueryApi.Customer.queries.Customer.queryKey, data);
                },
            }),
            StartTelephoneVerification: mutationOptions({
                mutationKey: ['START_TELEPHONE_VERIFICATION'],
                mutationFn: startUserTelephoneVerification,
                onSuccess: (data) => {
                    getQueryClient().setQueryData(
                        QueryApi.Customer.queries.TelephoneVerificationStatus.queryKey,
                        data,
                    );
                },
            }),
            FinishTelephoneVerification: mutationOptions({
                mutationKey: ['FINISH_TELEPHONE_VERIFICATION'],
                mutationFn: async ({ verificationCode }: { telephone: string; verificationCode: string }) => {
                    const result = await finishUserTelephoneVerification(verificationCode);
                    if (!result.success) {
                        throw new Error('INVALID_CODE');
                    }
                },
                onSuccess: (_, variables) => {
                    const queryClient = getQueryClient();
                    queryClient.setQueryData(QueryApi.Customer.queries.TelephoneVerificationStatus.queryKey, {
                        isVerified: true,
                        nextRetryAt: null,
                    });
                    const customerData = queryClient.getQueryData(
                        QueryApi.Customer.queries.Customer.queryKey,
                    )!;
                    const [telephonePrefix, telephone] = variables.telephone.split(' ');
                    queryClient.setQueryData(QueryApi.Customer.queries.Customer.queryKey, {
                        ...customerData,
                        telephonePrefix,
                        telephone,
                    });
                },
            }),
            RefreshToken: mutationOptions({
                mutationKey: ['REFRESH_CUSTOMER_TOKEN'],
                mutationFn: refreshCustomerAccessToken,
            }),
            AddItemToFavorite: mutationOptions({
                mutationKey: ['ADD_ITEM_TO_FAVORITE'],
                mutationFn: addCustomerFavoriteItem,
                onMutate: (variables) => {
                    const queryClient = getQueryClient();
                    queryClient.setQueriesData(
                        {
                            queryKey: ['CUSTOMER_FAVORITE_ITEMS'],
                        },
                        (oldData: { items: { itemId: string }[] }) => {
                            if (!oldData) return oldData;
                            return {
                                ...oldData,
                                items: [...oldData.items, { itemId: variables.itemId }],
                            };
                        },
                    );
                },
            }),
            RemoveItemFromFavorite: mutationOptions({
                mutationKey: ['REMOVE_ITEM_FROM_FAVORITE'],
                mutationFn: removeCustomerFavoriteItem,
                onMutate: (variables) => {
                    const queryClient = getQueryClient();
                    queryClient.setQueriesData(
                        {
                            queryKey: ['CUSTOMER_FAVORITE_ITEMS'],
                        },
                        (oldData: { items: { itemId: string }[] }) => {
                            if (!oldData) return oldData;
                            return {
                                ...oldData,
                                items: oldData.items.filter((item) => item.itemId !== variables.itemId),
                            };
                        },
                    );
                },
            }),
            CreateAddress: mutationOptions({
                mutationKey: ['ADD_ADDRESS'],
                mutationFn: async (address: Parameters<typeof createCustomerAddress>[0]) => {
                    await createCustomerAddress(address);
                    const queryClient = getQueryClient();
                    queryClient.invalidateQueries({ queryKey: ['CUSTOMER_ADDRESSES'] });
                    const addresses = await queryClient.fetchQuery({
                        queryKey: ['CUSTOMER_ADDRESSES'],
                        queryFn: () => getCustomerAddresses({ offset: 0, limit: PAGINATION_LIMIT }),
                    });
                    return addresses.items[0];
                },
            }),
            UpdateAddress: mutationOptions({
                mutationKey: ['UPDATE_ADDRESS'],
                mutationFn: updateCustomerAddress,
                onMutate: (variables) => {
                    const queryClient = getQueryClient();
                    queryClient.setQueriesData(
                        { queryKey: ['CUSTOMER_ADDRESSES'] },
                        (
                            oldData:
                                | { items: { id: string; isDefault: boolean }[]; total: number }
                                | undefined,
                        ) => {
                            if (!oldData) return oldData;
                            return {
                                ...oldData,
                                items: oldData.items.map((item) => {
                                    if (item.id === variables.addressId) {
                                        return { ...item, ...variables };
                                    }
                                    // Pokud nastavujeme tuto adresu jako defaultní, ostatní musíme zrušit
                                    if (variables.isDefault) {
                                        return { ...item, isDefault: false };
                                    }
                                    return item;
                                }),
                            };
                        },
                    );
                },
                onSuccess: () => {
                    getQueryClient().invalidateQueries({ queryKey: ['CUSTOMER_ADDRESSES'] });
                },
            }),
            DeleteAddress: mutationOptions({
                mutationKey: ['DELETE_ADDRESS'],
                mutationFn: deleteCustomerAddress,
                onMutate: (variables) => {
                    const queryClient = getQueryClient();
                    queryClient.setQueriesData(
                        { queryKey: ['CUSTOMER_ADDRESSES'] },
                        (oldData: { items: { id: string }[]; total: number } | undefined) => {
                            if (!oldData) return oldData;
                            return {
                                ...oldData,
                                items: oldData.items.filter((item) => item.id !== variables.addressId),
                                total: Math.max(0, (oldData.total || 0) - 1),
                            };
                        },
                    );
                },
                onSuccess: () => {
                    getQueryClient().invalidateQueries({ queryKey: ['CUSTOMER_ADDRESSES'] });
                },
            }),
            RequestPasswordReset: mutationOptions({
                mutationKey: ['REQUEST_PASSWORD_RESET'],
                mutationFn: requestPasswordReset,
            }),
            ResetPassword: mutationOptions({
                mutationKey: ['RESET_PASSWORD'],
                mutationFn: resetPassword,
            }),
            UploadAvatar: mutationOptions({
                mutationKey: ['UPLOAD_CUSTOMER_AVATAR'],
                mutationFn: async (file: File) => {
                    const uploadInfo = await createCustomerAvatarUploadUrl();
                    if (!uploadInfo) throw new Error('AVATAR_UPLOAD_URL_FAILED');

                    const formData = new FormData();
                    formData.append('file', file);
                    const uploadResponse = await fetch(uploadInfo.uploadUrl, {
                        method: 'PUT',
                        headers: { Authorization: `Bearer ${uploadInfo.jwt}` },
                        body: formData,
                    });
                    if (!uploadResponse.ok) {
                        throw new Error(`AVATAR_UPLOAD_FAILED_${uploadResponse.status}`);
                    }
                    const uploadResult = (await uploadResponse.json()) as { resultMeta: string };
                    const finalized = await finalizeCustomerAvatarUpload(uploadResult.resultMeta);
                    if (!finalized) throw new Error('AVATAR_FINALIZE_FAILED');
                    return finalized;
                },
                onSuccess: (data) => {
                    getQueryClient().setQueryData(QueryApi.Customer.queries.Customer.queryKey, (old) =>
                        old ? { ...old, avatarUrl: data.fullUrl } : old,
                    );
                },
            }),
            RemoveAvatar: mutationOptions({
                mutationKey: ['REMOVE_CUSTOMER_AVATAR'],
                mutationFn: async () => {
                    const ok = await deleteCustomerAvatar();
                    if (!ok) {
                        throw new Error('AVATAR_DELETE_FAILED');
                    }
                },
                onSuccess: () => {
                    getQueryClient().setQueryData(QueryApi.Customer.queries.Customer.queryKey, (old) =>
                        old ? { ...old, avatarUrl: undefined } : old,
                    );
                },
            }),
        },
    },
    Geolocation: {
        queries: {
            Geocode: ({
                query,
                countries,
                locationBias,
            }: {
                query: string;
                countries?: Country[];
                locationBias?: { lat: number; lon: number };
            }) =>
                queryOptions({
                    queryKey: ['GEOCODE', query, countries, locationBias],
                    queryFn: () => geocodeAddress({ query, countries, locationBias }),
                }),
            ReverseGeocode: ({ lat, lon }: { lat: number; lon: number }) =>
                queryOptions({
                    queryKey: ['REVERSE_GEOCODE', lat, lon],
                    queryFn: () => reverseGeocodeAddress({ lat, lon }),
                }),
        },
    },
    Order: {
        queries: {
            Order: Object.assign(
                (orderId: string) =>
                    queryOptions({
                        queryKey: ['ORDER', orderId],
                        queryFn: () => getOrder(orderId),
                    }),
                { queryKey: ['ORDER'] as const },
            ),
            TimedOrderDiscount: ({
                deliveryMethodId,
                dateTime,
                latitude,
                longitude,
            }: TimedOrderDiscountParameters) =>
                queryOptions({
                    queryKey: ['TIMED_ORDERS', deliveryMethodId, dateTime, latitude, longitude],
                    queryFn: () => getTimedOrderDiscount({ deliveryMethodId, dateTime, latitude, longitude }),
                    staleTime: 10_000,
                }),
            // ponytail: one server action / many REST checks until API has a real batch
            TimedOrderDiscounts: ({
                deliveryMethodId,
                dateTimes,
                latitude,
                longitude,
            }: TimedOrderDiscountsParameters) =>
                queryOptions({
                    queryKey: [
                        'TIMED_ORDERS_BATCH',
                        deliveryMethodId,
                        latitude,
                        longitude,
                        dateTimes.join(','),
                    ],
                    queryFn: () =>
                        getTimedOrderDiscounts({ deliveryMethodId, dateTimes, latitude, longitude }),
                    staleTime: 10_000,
                }),
            GuestTelephoneVerificationStatus: (telephone: string) =>
                queryOptions({
                    queryKey: ['GUEST_TELEPHONE_VERIFICATION_STATUS', telephone],
                    queryFn: () =>
                        getIsTelephoneVerified(
                            telephone,
                            window.localStorage.getItem(VERIFIED_PHONE_TOKEN_STORAGE_KEY) ?? undefined,
                        ),
                    staleTime: 5_000,
                }),
        },
        mutations: {
            Create: mutationOptions({
                mutationKey: ['CREATE_ORDER'],
                mutationFn: (order: Parameters<typeof createOrder>[0]) =>
                    createOrder(
                        order,
                        window.localStorage.getItem(VERIFIED_PHONE_TOKEN_STORAGE_KEY) ?? undefined,
                    ),
                onSuccess: (data) => {
                    if (data?.verifiedPhoneToken) {
                        window.localStorage.setItem(
                            VERIFIED_PHONE_TOKEN_STORAGE_KEY,
                            data.verifiedPhoneToken,
                        );
                    }
                    getQueryClient().invalidateQueries({ queryKey: ['CUSTOMER_ORDERS'] });
                },
            }),
            StartGuestTelephoneVerification: mutationOptions({
                mutationKey: ['START_GUEST_TELEPHONE_VERIFICATION'],
                mutationFn: startTelephoneVerification,
                onSuccess: (data, telephone) => {
                    getQueryClient().setQueryData(['GUEST_TELEPHONE_VERIFICATION_STATUS', telephone], data);
                },
            }),
            FinishGuestTelephoneVerification: mutationOptions({
                mutationKey: ['FINISH_GUEST_TELEPHONE_VERIFICATION'],
                mutationFn: ({
                    telephone,
                    verificationCode,
                }: {
                    telephone: string;
                    verificationCode: string;
                }) => finishTelephoneVerification(telephone, verificationCode),
                onSuccess: (data, { telephone }) => {
                    if (data?.verifiedPhoneToken) {
                        window.localStorage.setItem(
                            VERIFIED_PHONE_TOKEN_STORAGE_KEY,
                            data?.verifiedPhoneToken,
                        );
                    } else {
                        window.localStorage.removeItem(VERIFIED_PHONE_TOKEN_STORAGE_KEY);
                    }

                    getQueryClient().setQueryData(['GUEST_TELEPHONE_VERIFICATION_STATUS', telephone], {
                        isVerified: true,
                        nextRetryAt: null,
                    });
                },
            }),
        },
    },
    Currency: {
        queries: {
            GetCurrency: queryOptions({
                queryKey: ['GET_CURRENCY'],
                queryFn: async () => {
                    return Cookies.get('currency') || '';
                },
            }),
        },
        mutations: {
            ChangeCurrency: mutationOptions({
                mutationKey: ['CHANGE_CURRENCY'],
                mutationFn: async (currency: string) => {
                    Cookies.set('currency', currency);
                    return currency;
                },
                onMutate: (currency) => {
                    const queryClient = getQueryClient();
                    queryClient.setQueryData(QueryApi.Currency.queries.GetCurrency.queryKey, currency);
                },
            }),
        },
    },
    Outages: {
        queries: {
            Outages: queryOptions({
                queryKey: ['OUTAGES'],
                queryFn: getOutages,
            }),
        },
    },
    CMS: {
        queries: {
            TermsAndConditions: (locale: Language) =>
                queryOptions({
                    queryKey: ['CMS_TERMS_AND_CONDITIONS', locale],
                    queryFn: async () => {
                        try {
                            return await apiFetch<{ translation: { content: string; title: string } }>(
                                'https://api.ordermage.com/api/public/blogs/1ca289f5-17a8-4a63-b0e8-c6292aa23c32?includeInternal=true&ignorePublished=false',
                                {
                                    method: 'GET',
                                    headers: {
                                        accept: 'application/json',
                                        'Accept-Language': locale === Language.CS ? 'cs' : 'en',
                                    },
                                },
                            );
                        } catch (e) {
                            logger.error({ err: e }, '[CMS.TermsAndConditions] request failed');
                            throw e;
                        }
                    },
                }),
        },
    },
    Country: {
        queries: {
            GetCountry: queryOptions({
                queryKey: ['GET_COUNTRY'],
                queryFn: async () => {
                    return Cookies.get('country') || '';
                },
            }),
        },
        mutations: {
            ChangeCountry: mutationOptions({
                mutationKey: ['CHANGE_COUNTRY'],
                mutationFn: async (country: string) => {
                    Cookies.set('country', country);
                    return country;
                },
                onMutate: (country) => {
                    const queryClient = getQueryClient();
                    queryClient.setQueryData(QueryApi.Country.queries.GetCountry.queryKey, country);
                },
            }),
        },
    },
    Blog: {
        queries: {
            BlogPosts: (locale: Language) =>
                queryOptions({
                    queryKey: ['BLOG_POSTS', locale],
                    queryFn: () => getBlogPosts(locale),
                }),
            BlogPost: (locale: Language, slug: string) =>
                queryOptions({
                    queryKey: ['BLOG_POST', locale, slug],
                    queryFn: () => getBlogPost(locale, slug),
                }),
        },
    },
    CustomerNotifications: {
        queries: {
            List: ({
                venueId,
                limit = CUSTOMER_NOTIFICATIONS_LIST_LIMIT,
                offset = 0,
                authenticated = false,
            }: GetCustomerNotificationsParams & { authenticated?: boolean } = {}) =>
                queryOptions({
                    queryKey: [
                        ...customerNotificationsQueryKeyRoot,
                        authenticated ? 'auth' : 'guest',
                        venueId,
                        limit,
                        offset,
                    ],
                    queryFn: () =>
                        authenticated
                            ? getAuthenticatedCustomerNotifications({ venueId, limit, offset })
                            : getCustomerNotifications({ venueId, limit, offset }),
                    // Socket invalidation is the refresh path; remount refetch + Server Action
                    // soft-nav caused GET/POST loops on contact.
                    staleTime: Infinity,
                    refetchOnMount: false,
                    placeholderData: (previousData) => previousData,
                }),
        },
    },
};
